import axios from 'axios';
import { useMemo } from 'react';
import { useUser } from '../contexts/UserContext';
import {
  AdductData,
  AnalyteData,
  AnalyteUpdate,
  BaseSpectralLibraryResponse,
  ChangeLogResponse,
  CreateAnalyteData,
  LipidMapsClassification,
  MethodData,
  MethodUpdate,
  ProductData,
  Tag,
} from '../interfaces/compound.db';
import { PaginatedResponse } from '../interfaces/misc';
import { PermissionString, READ_CANDLES } from '../utils/permissions';

export class CompoundDBClient {
  private baseUrl = '/api/compound';
  private hasPermission: (permission: PermissionString) => boolean;

  constructor(hasPermission: (permission: PermissionString) => boolean) {
    this.hasPermission = hasPermission;
  }

  async getAllAnalytes(): Promise<AnalyteData[]> {
    try {
      const response = await axios.get<AnalyteData[]>(
        `${this.baseUrl}/analytes`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching all analytes:', error);
      throw error;
    }
  }

  async checkDuplicatePubChemCIDs(pubchemCids: number[]): Promise<number[]> {
    try {
      const response = await axios.post<number[]>(
        `${this.baseUrl}/analytes/check-duplicates`,
        { pubchem_cids: pubchemCids }
      );
      return response.data;
    } catch (error) {
      console.error('Error checking duplicate PubChem CIDs:', error);
      throw error;
    }
  }

  async getAnalyteInfo(analyteId: string): Promise<AnalyteData> {
    try {
      const response = await axios.get<AnalyteData>(
        `${this.baseUrl}/analytes/${analyteId}/info`,
        { headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' } }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching analyte info:', error);
      throw error;
    }
  }

  async getAnalyteBaseInfo(analyteId: string): Promise<AnalyteData> {
    try {
      const response = await axios.get<AnalyteData>(
        `${this.baseUrl}/analytes/${analyteId}/base/info`,
        { headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' } }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching analyte info:', error);
      throw error;
    }
  }

  async getAnalyteHistory(
    analyteId: string,
    skip: number = 0,
    limit: number = 20
  ): Promise<ChangeLogResponse> {
    try {
      const response = await axios.get<ChangeLogResponse>(
        `${this.baseUrl}/history/entity/${analyteId}/diffs`,
        { params: { skip, limit } }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching analyte history:', error);
      throw error;
    }
  }

  async getAnalytes(analyteIds: string[]): Promise<AnalyteData[]> {
    // The endpoint leaves candles out unless asked, and asking needs read:candles.
    const analyteTypes = this.hasPermission(READ_CANDLES)
      ? 'analyte_types=analyte&analyte_types=candle'
      : 'analyte_types=analyte';
    try {
      const response = await axios.post<AnalyteData[]>(
        `${this.baseUrl}/analytes/list?${analyteTypes}`,
        { analyte_ids: analyteIds }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching analytes:', error);
      throw error;
    }
  }

  async getBaseAnalytes(analyteIds?: string[]): Promise<AnalyteData[]> {
    try {
      const response = await axios.post<AnalyteData[]>(
        `${this.baseUrl}/analytes/base/list`,
        { analyte_ids: analyteIds }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching base analytes:', error);
      throw error;
    }
  }

  async searchAnalytes(
    searchQuery?: string,
    currentPage?: number,
    rowsPerPage?: number,
    filterString?: string,
    sortDirection?: 'asc' | 'desc',
    sortedColumn?: string
  ): Promise<PaginatedResponse<AnalyteData>> {
    const include_candles = this.hasPermission(READ_CANDLES);
    const baseUrl = this.baseUrl + '/analytes/search?';
    const analyteNames = searchQuery ? `analyte_names=${searchQuery}&` : '';
    const skip =
      currentPage && rowsPerPage
        ? `skip=${(currentPage - 1) * rowsPerPage}&`
        : '';
    const limit = rowsPerPage ? `limit=${rowsPerPage}&` : '';
    const candles = include_candles
      ? `include_candles=${include_candles}&`
      : '';
    // Only include sort parameters if both are provided
    const sort =
      sortedColumn && sortDirection
        ? `sorted_column=${sortedColumn}&sort_direction=${sortDirection}&`
        : '';

    const url = `${baseUrl}${analyteNames}${skip}${limit}${candles}${sort}${filterString}`;

    try {
      const response = await axios.get<PaginatedResponse<AnalyteData>>(url);
      return response.data;
    } catch (error) {
      console.error('Error searching analytes:', error);
      throw error;
    }
  }

  async getMethods(): Promise<MethodData[]> {
    try {
      const response = await axios.get<MethodData[]>(`${this.baseUrl}/methods`);
      return response.data;
    } catch (error) {
      console.error('Error fetching methods:', error);
      throw error;
    }
  }

  async getTags(
    methods: string[] = [],
    requireEmbedding: boolean = false
  ): Promise<Tag[]> {
    try {
      let url = `${this.baseUrl}/tags`;
      const params = new URLSearchParams();
      if (methods.length > 0) {
        methods.forEach((method) => params.append('method', method));
      }
      if (requireEmbedding) {
        params.append('require_embedding', 'true');
      }
      const response = await axios.get<Tag[]>(url, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching tags:', error);
      throw error;
    }
  }

  async getAdducts(): Promise<AdductData[]> {
    try {
      const response = await axios.get<AdductData[]>(`${this.baseUrl}/adducts`);
      return response.data;
    } catch (error) {
      console.error('Error fetching adducts:', error);
      throw error;
    }
  }

  async getProducts(): Promise<ProductData[]> {
    try {
      const response = await axios.get<ProductData[]>(
        `${this.baseUrl}/products`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  }

  async postAnalytes(analyteData: CreateAnalyteData[]): Promise<any> {
    try {
      const response = await axios.post(`${this.baseUrl}/analytes`, {
        analytes: analyteData,
      });
      return response.data;
    } catch (error) {
      console.error('Error creating analyte:', error);
      throw error;
    }
  }

  async putAnalyteInfo(
    analyteId: string,
    analyteData: AnalyteUpdate
  ): Promise<any> {
    try {
      const response = await axios.put(
        `${this.baseUrl}/analytes/${analyteId}`,
        analyteData
      );
      return response.data;
    } catch (error) {
      console.error('Error updating analyte info:', error);
      throw error;
    }
  }

  async putAnalyteMethod(
    analyteId: string,
    methodId: string,
    methodData: MethodUpdate
  ): Promise<any> {
    try {
      const response = await axios.put(
        `${this.baseUrl}/analytes/${analyteId}/method/${methodId}`,
        methodData
      );
      return response.data;
    } catch (error) {
      console.error('Error updating analyte method:', error);
      throw error;
    }
  }

  async deleteAnalyteMethod(analyteId: string, methodId: string): Promise<any> {
    try {
      const response = await axios.delete(
        `${this.baseUrl}/analytes/${analyteId}/method/${methodId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting method:', error);
      throw error;
    }
  }

  async deleteAnalyteProduct(
    analyteId: string,
    productId: string
  ): Promise<any> {
    try {
      const response = await axios.delete(
        `${this.baseUrl}/analytes/${analyteId}/use/product/${productId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  }

  async deleteAnalyteTag(analyteId: string, tagName: string): Promise<any> {
    try {
      const response = await axios.delete(
        `${this.baseUrl}/analytes/${analyteId}/tag/${tagName}`
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting analyte tag:', error);
      throw error;
    }
  }

  async bulkTagAnalytes(
    analyteIds: string[],
    tagNames: string[]
  ): Promise<{
    message: string;
    analytes_modified: number;
    total_analytes: number;
  }> {
    try {
      const response = await axios.put(`${this.baseUrl}/analytes/tag`, {
        analyte_ids: analyteIds,
        tag_names: tagNames,
      });
      return response.data;
    } catch (error) {
      console.error('Error bulk tagging analytes:', error);
      throw error;
    }
  }

  async getLipidClassificationsByBondType(
    level3BondType: string,
    limit: number = 1000,
    offset: number = 0
  ): Promise<LipidMapsClassification[]> {
    try {
      const response = await axios.get<LipidMapsClassification[]>(
        `${this.baseUrl}/lipid-classifications`,
        {
          params: {
            level_3_bond_type: level3BondType,
            limit,
            offset,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error(
        'Error fetching lipid classifications by bond type:',
        error
      );
      throw error;
    }
  }

  async getSpectralLibrary(
    id: string
  ): Promise<BaseSpectralLibraryResponse | null> {
    try {
      const response = await axios.get<BaseSpectralLibraryResponse>(
        `${this.baseUrl}/spectral_library/${id}/base`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      console.error('Error fetching spectral library:', error);
      throw error;
    }
  }
}

// Custom hook that creates and returns the API client
export const useCompoundDB = () => {
  const { hasPermission } = useUser();

  // Use useMemo to prevent unnecessary re-creation of the client
  const client = useMemo(() => {
    return new CompoundDBClient(hasPermission);
  }, [hasPermission]);

  return client;
};
